'use client'

import { clsx } from 'clsx'
import NextImage, { type ImageProps } from 'next/image'
import { type FC, useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'

import { IconPause, IconPlay } from '../../icons'

type InteractiveGifProps = Omit<ImageProps, 'unoptimized'>

const getImageSrc = (
  src: ImageProps['src'],
  staticImage: string,
): ImageProps['src'] => {
  if (typeof src === 'string') {
    return staticImage
  }

  const staticImport = 'default' in src ? src.default : src

  return {
    ...staticImport,
    src: staticImage,
  }
}

const InteractiveGif: FC<InteractiveGifProps> = ({ alt, className, src }) => {
  const [isPlaying, setIsPlaying] = useState(true)
  const [staticFrameUrl, setStaticFrameUrl] = useState<string | null>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const imageRef = useRef<HTMLImageElement>(null)

  const intl = useIntl()

  /**
   *  Extract the first frame of a GIF
   *  Draw it on the canvas and use it as a static image (png).
   */
  useEffect(() => {
    const canvas = canvasRef.current
    const image = imageRef.current
    if (!canvas || !image) {
      return
    }

    const ctx = canvas.getContext('2d')
    if (!ctx) {
      return
    }

    const handleImageLoad = (): void => {
      canvas.width = image.naturalWidth
      canvas.height = image.naturalHeight
      ctx.drawImage(image, 0, 0)

      try {
        setStaticFrameUrl(canvas.toDataURL('image/png'))
      } catch {
        // If the image is cross-origin, toDataURL will throw an error
        // and we can't use it as a static image.
        setStaticFrameUrl(null)
      }
    }

    const handleImageError = (): void => {
      setStaticFrameUrl(null)
    }

    if (image.complete) {
      handleImageLoad()
    } else {
      image.addEventListener('load', handleImageLoad)
      image.addEventListener('error', handleImageError)
    }

    return (): void => {
      image.removeEventListener('load', handleImageLoad)
      image.removeEventListener('error', handleImageError)
    }
  }, [src])

  const togglePlayback = (): void => {
    setIsPlaying((prev) => !prev)
  }

  return (
    <div className={clsx('group relative inline-block', className)}>
      <canvas className="hidden" ref={canvasRef} />
      <NextImage
        alt={alt}
        // crossOrigin="anonymous" is required to extract the first frame of the GIF using canvas
        // Without this attribute, the canvas becomes "tainted" when drawing cross-origin images
        // Browser security policies block toDataURL() on tainted canvases to prevent data theft
        // This ensures the server provides proper CORS headers allowing canvas data extraction
        crossOrigin="anonymous"
        ref={imageRef}
        src={
          !isPlaying && staticFrameUrl ? getImageSrc(src, staticFrameUrl) : src
        }
        unoptimized
      />
      <button
        aria-label={
          isPlaying
            ? intl.formatMessage({ id: 'general.media.pause' })
            : intl.formatMessage({ id: 'general.media.play' })
        }
        className={clsx(
          'absolute bottom-2 left-2 flex cursor-pointer items-center justify-center',
          isPlaying ? 'group-hover:flex' : 'flex',
          !staticFrameUrl && 'hidden group-hover:hidden',
        )}
        onClick={togglePlayback}
        type="button"
      >
        {isPlaying ? (
          <IconPause className="size-6 fill-dark-brand" />
        ) : (
          <IconPlay className="size-6 fill-dark-brand" />
        )}
      </button>
    </div>
  )
}

export { InteractiveGif }
